import React, { createContext, useContext, useState, useEffect } from "react";
import { getAccessToken, isAuthenticated, clearTokens } from "../utils/auth";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticatedState, setIsAuthenticatedState] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (isAuthenticated()) {
          setIsAuthenticatedState(true);
        } else {
          // Try to get a new token
          await getAccessToken();
          setIsAuthenticatedState(true);
        }
      } catch (err) {
        console.error("Authentication check failed:", err);
        setError("Failed to authenticate");
        setIsAuthenticatedState(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async () => {
    try {
      setError(null);
      setIsLoading(true);
      await getAccessToken();
      setIsAuthenticatedState(true);
    } catch (err) {
      console.error("Login failed:", err);
      setError("Login failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearTokens();
    setIsAuthenticatedState(false);
    setError(null);
  };

  const refreshToken = async () => {
    try {
      setError(null);
      await getAccessToken();
      setIsAuthenticatedState(true);
    } catch (err) {
      console.error("Token refresh failed:", err);
      setError("Token refresh failed");
      setIsAuthenticatedState(false);
      throw err;
    }
  };

  const value = {
    isAuthenticated: isAuthenticatedState,
    isLoading,
    error,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
